/**
 * PolygonProcessingPipeline.ts
 *
 * Specialized pipeline for processing polygons with support for:
 * - Per-polygon and batch processing modes
 * - Conditional processing based on polygon properties
 * - Recursive child (hole) processing
 * - Point caching for geometric consistency
 */

import { BasePipeline, ProcessingPass, ProcessOptions, ProcessorFunction } from './BasePipeline';
import { Polygon, Point2D, createPolygon } from './segmentation/types';

/**
 * Options for polygon processing
 */
export interface PolygonProcessOptions extends ProcessOptions {
  /** Whether to apply simplification passes (used by condition functions) */
  simplify?: boolean;
}

/**
 * Polygon-specific processor function types
 */
export type PolygonProcessor = (polygon: Polygon, kwargs?: Record<string, unknown>) => Polygon | Polygon[];
export type PolygonBatchProcessor = (polygons: Polygon[], kwargs?: Record<string, unknown>) => Polygon[];

/**
 * Polygon-specific condition function
 */
export type PolygonCondition = (polygon: Polygon, simplify: boolean) => boolean;

/**
 * Registry entry for polygon processors
 */
export interface ProcessorRegistryEntry {
  func: PolygonProcessor | PolygonBatchProcessor;
  conditionFunc?: PolygonCondition;
  batchProcessor?: boolean;
  processesChildren?: boolean;
}

/**
 * Processing pipeline specialized for polygons
 * TItem = Polygon for per-item conditions
 */
export class PolygonProcessingPipeline extends BasePipeline<Polygon[], Polygon[], Polygon[], Record<string, unknown>, Polygon> {
  // Point cache: Maps grid-snapped coordinates to original positions (per pass)
  private pointCaches: Map<string, Map<string, Point2D>> = new Map();
  private pointCacheGridSize = 1.0; // Default grid size in same units as points (e.g., 1cm if points are in meters)

  /**
   * Set the grid size for point caching (in same units as polygon points)
   */
  setPointCacheGridSize(gridSize: number): this {
    this.pointCacheGridSize = gridSize;
    return this;
  }

  /**
   * Get the grid size for point caching (in same units as polygon points)
   */
  getPointCacheGridSize(): number {
    return this.pointCacheGridSize;
  }

  /**
   * Export point caches (for persistence across pipeline recreations)
   */
  exportPointCaches(): Map<string, Map<string, Point2D>> {
    return this.pointCaches;
  }

  /**
   * Import point caches (restore from previous pipeline instance)
   */
  importPointCaches(caches: Map<string, Map<string, Point2D>>): this {
    this.pointCaches = caches;
    return this;
  }

  /**
   * Clear all point caches. The cache snaps a pass's output points onto
   * previously-seen nearby points for temporal stability (good for the
   * interactive line tool). Callers that process INDEPENDENT shapes — where
   * one run must not be contaminated by a previous run's points (e.g. the
   * Wall Tracer, whose hover preview and click-commit must match exactly) —
   * clear the cache before each run.
   */
  clearPointCaches(): void {
    this.pointCaches.clear();
  }

  /**
   * Convert input to context (identity transform for polygons)
   */
  protected async inputToContext(input: Polygon[]): Promise<Polygon[]> {
    return input;
  }

  /**
   * Convert context to output (identity transform for polygons)
   */
  protected async contextToOutput(context: Polygon[]): Promise<Polygon[]> {
    return context;
  }

  /**
   * Override process method to handle per-polygon vs batch processing
   */
  async process(input: Polygon[], options?: PolygonProcessOptions): Promise<Polygon[]> {
    let context = await this.inputToContext(input);
    this.passTimings.clear();
    const simplify = options?.simplify ?? true;

    for (const pass of this.passes) {
      // Check for abort signal
      if (options?.signal?.aborted) {
        throw new Error(`Pipeline aborted before pass: ${pass.name}`);
      }

      // Skip disabled passes
      if (!this.enabledPasses.has(pass.name)) {
        this.logPassSkipped(pass.name);
        continue;
      }

      const startTime = performance.now();
      this.logPassStart(pass.name);

      try {
        // Batch processor: processes all polygons at once
        if (pass.batchProcessor) {
          context = await this.processBatch(context, pass, options);
        } else {
          // Per-polygon processor
          context = await this.processPerItem(context, pass, simplify, options);
        }

        // Apply point caching after each pass
        context = await this.afterPass(context, pass);
      } catch (e) {
        this.logPassFailed(pass.name, e);
        // Continue to next pass
      }

      const elapsedTime = performance.now() - startTime;
      this.passTimings.set(pass.name, elapsedTime);
      this.logPassComplete(pass.name, elapsedTime);
    }

    return this.contextToOutput(context);
  }

  /**
   * Process polygons in batch mode
   */
  private async processBatch(polygons: Polygon[], pass: ProcessingPass<Polygon[], Record<string, unknown>, Polygon>, options?: PolygonProcessOptions): Promise<Polygon[]> {
    const processor = pass.processor as PolygonBatchProcessor;
    const result = processor(polygons, pass.kwargs);
    return result instanceof Promise ? await result : result;
  }

  /**
   * Process polygons one by one
   */
  private async processPerItem(polygons: Polygon[], pass: ProcessingPass<Polygon[], Record<string, unknown>, Polygon>, simplify: boolean, options?: PolygonProcessOptions): Promise<Polygon[]> {
    const processor = pass.processor as unknown as PolygonProcessor;
    const condition = pass.condition as PolygonCondition | undefined;
    const nextBatch: Polygon[] = [];

    for (let i = 0; i < polygons.length; i++) {
      // Check if aborted during processing
      if (options?.signal?.aborted) {
        throw new Error(`Pipeline aborted during pass: ${pass.name}`);
      }

      const polygon = polygons[i];

      // Check if condition is met
      if (condition && !condition(polygon, simplify)) {
        nextBatch.push(polygon);
        continue;
      }

      // Apply processor
      try {
        const result = processor(polygon, pass.kwargs);
        const resultPolygons = Array.isArray(result) ? result : [result];

        // Auto-process children (holes) unless pass handles them itself
        if (!pass.processesChildren) {
          for (const resultPoly of resultPolygons) {
            if (polygon.children && polygon.children.length > 0) {
              // Apply same processor to each child (hole)
              const processedChildren: Polygon[] = [];
              for (const child of polygon.children) {
                try {
                  const childResult = processor(child, pass.kwargs);
                  if (Array.isArray(childResult)) {
                    processedChildren.push(...childResult);
                  } else {
                    processedChildren.push(childResult);
                  }
                } catch (e) {
                  this.logChildProcessingFailed(e);
                  processedChildren.push(child);
                }
              }

              // Assign processed children to result polygon
              if (processedChildren.length > 0) {
                resultPoly.children = processedChildren;
              }
            }
          }
        }

        nextBatch.push(...resultPolygons);
      } catch (e) {
        this.logItemProcessingFailed(i, e);
        nextBatch.push(polygon);
      }
    }

    return nextBatch;
  }

  /**
   * Apply point caching after each pass
   */
  protected async afterPass(context: Polygon[], pass: ProcessingPass<Polygon[], Record<string, unknown>, Polygon>): Promise<Polygon[]> {
    return this.applyPointCache(context, pass.name);
  }

  /**
   * Apply point caching to polygons after a processing pass
   */
  private applyPointCache(polygons: Polygon[], passName: string): Polygon[] {
    // Get or create cache for this pass
    if (!this.pointCaches.has(passName)) {
      this.pointCaches.set(passName, new Map());
    }
    const cache = this.pointCaches.get(passName);
    if (!cache) return polygons;
    const accessedHashes = new Set<string>();

    // Process all polygons recursively
    const processPolygon = (polygon: Polygon): Polygon => {
      const newPoints: Point2D[] = [];

      for (const point of polygon.points) {
        const hash = this.hashPoint(point);

        // Check for cached point in neighboring cells
        const cachedPoint = this.findClosestCachedPoint(point, cache);

        if (cachedPoint) {
          // Use cached position and mark its hash as accessed
          const cachedHash = this.hashPoint(cachedPoint);
          accessedHashes.add(cachedHash);
          newPoints.push(cachedPoint);
        } else {
          // New point - add to cache
          accessedHashes.add(hash);
          cache.set(hash, point);
          newPoints.push(point);
        }
      }

      // Create new polygon with cached points
      const newPolygon = createPolygon(newPoints, polygon.isHole);

      // Process children recursively
      if (polygon.children && polygon.children.length > 0) {
        newPolygon.children = polygon.children.map(processPolygon);
      }

      return newPolygon;
    };

    const result = polygons.map(processPolygon);

    // Clean up unused cache entries
    const keysToDelete: string[] = [];
    for (const key of cache.keys()) {
      if (!accessedHashes.has(key)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => cache.delete(key));

    return result;
  }

  /**
   * Hash a point to grid coordinates
   */
  private hashPoint(point: Point2D): string {
    const gridX = Math.round(point.x / this.pointCacheGridSize);
    const gridY = Math.round(point.y / this.pointCacheGridSize);
    return `${gridX},${gridY}`;
  }

  /**
   * Get neighboring grid cells (including current cell) - 3x3 grid
   */
  private getNeighborHashes(point: Point2D): string[] {
    const gridX = Math.round(point.x / this.pointCacheGridSize);
    const gridY = Math.round(point.y / this.pointCacheGridSize);

    const neighbors: string[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        neighbors.push(`${gridX + dx},${gridY + dy}`);
      }
    }
    return neighbors;
  }

  /**
   * Find closest cached point in neighboring grid cells
   */
  private findClosestCachedPoint(point: Point2D, cache: Map<string, Point2D>): Point2D | null {
    const neighbors = this.getNeighborHashes(point);
    let closestPoint: Point2D | null = null;
    let minDist = Infinity;

    // Maximum distance to consider a cache hit (1.5x grid size to cover diagonals)
    const maxDist = this.pointCacheGridSize * 1.5;

    for (const hash of neighbors) {
      const cachedPoint = cache.get(hash);
      if (cachedPoint) {
        const dx = point.x - cachedPoint.x;
        const dy = point.y - cachedPoint.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < minDist && dist <= maxDist) {
          minDist = dist;
          closestPoint = cachedPoint;
        }
      }
    }

    return closestPoint;
  }

  // Logging methods override

  protected override logPassFailed(name: string, error: unknown): void {
    console.warn(`      ⚠️  Pass ${name} failed:`, error);
  }

  private logChildProcessingFailed(error: unknown): void {
    console.warn(`      ⚠️  Failed on hole:`, error);
  }

  private logItemProcessingFailed(index: number, error: unknown): void {
    console.warn(`      ⚠️  Failed on polygon ${index}:`, error);
  }

  /**
   * Register a processor that works on individual polygons or batches
   * This is a type-safe wrapper that accepts polygon-specific functions
   */
  registerPolygonProcessor(
    processorId: string,
    func: PolygonProcessor | PolygonBatchProcessor,
    options: {
      conditionFunc?: PolygonCondition;
      batchProcessor?: boolean;
      processesChildren?: boolean;
      defaultKwargs?: Record<string, unknown>;
    } = {},
  ): this {
    // Register with base pipeline - the pipeline runtime handles both single-polygon
    // and batch processors correctly based on the batchProcessor flag
    return super.registerProcessor(processorId, func as unknown as ProcessorFunction<Polygon[], Record<string, unknown>>, {
      condition: options.conditionFunc,
      batchProcessor: options.batchProcessor,
      processesChildren: options.processesChildren,
      defaultKwargs: options.defaultKwargs,
    });
  }

  /**
   * Override loadFromFile to use default polygon pipeline config path
   */
  override async loadFromFile(url = '/config/segmentation/poly-pipeline-config.json'): Promise<boolean> {
    const success = await super.loadFromFile(url);
    if (success) {
      console.log(`[PolygonProcessingPipeline] Pipeline loaded from ${url}`);
    }
    return success;
  }
}
